import i18n from "@/i18n.ts";
import { ApiIdTitle } from "@/types/common.types.ts";
import {
  ApiWorkflowConditionEntityTypes,
  ApiWorkflowConditionFieldType,
  ApiWorkflowConditionFieldTypes,
  ApiWorkflowConditionFieldValue,
  ApiWorkflowConditionOperator,
  getApiWorkflowConditionFieldTypesTitle,
} from "@/types/workflows/workflow.condition.types.ts";
import * as yup from "yup";

export const buildConditionSchema = () =>
  yup.object({
    id: yup.number().required(i18n.t("errors:field_required")),
    entity_type: yup
      .mixed<ApiWorkflowConditionEntityTypes>()
      .oneOf(Object.values(ApiWorkflowConditionEntityTypes))
      .required(i18n.t("errors:field_required")),
    field_name: yup
      .mixed<ApiWorkflowConditionFieldTypes>()
      .oneOf(Object.values(ApiWorkflowConditionFieldTypes))
      .required(i18n.t("errors:field_required")),
    operator: yup
      .mixed<ApiWorkflowConditionOperator>()
      .oneOf(Object.values(ApiWorkflowConditionOperator))
      .required(i18n.t("errors:field_required")),
    field_value: yup.object({
      type: yup
        .mixed<ApiWorkflowConditionFieldType>()
        .oneOf(Object.values(ApiWorkflowConditionFieldType))
        .required(),
      value: yup.mixed<Exclude<ApiWorkflowConditionFieldValue, null>>().nullable().defined(),
    }),
    condition_group: yup.number().required(),
    condition_entities: yup.array().defined(),
  });

export const getOperatorsForFieldType = (
  fieldType: ApiWorkflowConditionFieldType
): ApiIdTitle[] => {
  const { t } = i18n;

  const commonOperators = [
    { id: ApiWorkflowConditionOperator.equals, title: t("workflow:operator_equals") },
    { id: ApiWorkflowConditionOperator.not_equals, title: t("workflow:operator_not_equals") },
    { id: ApiWorkflowConditionOperator.is_null, title: t("workflow:operator_is_null") },
    { id: ApiWorkflowConditionOperator.is_not_null, title: t("workflow:operator_is_not_null") },
  ];

  const numericOperators = [
    ...commonOperators,
    {
      id: ApiWorkflowConditionOperator.greater_than,
      title: t("workflow:operator_greater_than"),
    },
    {
      id: ApiWorkflowConditionOperator.greater_than_or_equal,
      title: t("workflow:operator_greater_than_or_equal"),
    },
    { id: ApiWorkflowConditionOperator.less_than, title: t("workflow:operator_less_than") },
    {
      id: ApiWorkflowConditionOperator.less_than_or_equal,
      title: t("workflow:operator_less_than_or_equal"),
    },
  ];

  const stringOperators = [
    ...commonOperators,
    { id: ApiWorkflowConditionOperator.contains, title: t("workflow:operator_contains") },
    { id: ApiWorkflowConditionOperator.not_contains, title: t("workflow:operator_not_contains") },
    { id: ApiWorkflowConditionOperator.starts_with, title: t("workflow:operator_starts_with") },
    { id: ApiWorkflowConditionOperator.ends_with, title: t("workflow:operator_ends_with") },
  ];

  const arrayOperators = [
    { id: ApiWorkflowConditionOperator.in_, title: t("workflow:operator_in") },
    { id: ApiWorkflowConditionOperator.not_in, title: t("workflow:operator_not_in") },
    { id: ApiWorkflowConditionOperator.is_null, title: t("workflow:operator_is_null") },
    { id: ApiWorkflowConditionOperator.is_not_null, title: t("workflow:operator_is_not_null") },
  ];

  switch (fieldType) {
    case ApiWorkflowConditionFieldType.string:
      return stringOperators;
    case ApiWorkflowConditionFieldType.integer:
    case ApiWorkflowConditionFieldType.decimal:
      return numericOperators;
    case ApiWorkflowConditionFieldType.boolean:
      return commonOperators.filter(
        (op) =>
          op.id === ApiWorkflowConditionOperator.equals ||
          op.id === ApiWorkflowConditionOperator.not_equals
      );
    case ApiWorkflowConditionFieldType.date:
    case ApiWorkflowConditionFieldType.datetime:
      return numericOperators;
    case ApiWorkflowConditionFieldType.array_integer:
    case ApiWorkflowConditionFieldType.array_string:
      return arrayOperators;
    default:
      return commonOperators;
  }
};

export type WorkflowConditionMode = "block" | "employee_trigger" | "time_off_trigger" | "candidate";

export const getAllowedConditionEntityTypesForMode = (
  mode: WorkflowConditionMode
): ApiWorkflowConditionEntityTypes[] => {
  switch (mode) {
    case "candidate":
      return [ApiWorkflowConditionEntityTypes.candidate, ApiWorkflowConditionEntityTypes.job];
    case "time_off_trigger":
      return [ApiWorkflowConditionEntityTypes.time_off, ApiWorkflowConditionEntityTypes.employee];
    case "block":
      return [ApiWorkflowConditionEntityTypes.employee, ApiWorkflowConditionEntityTypes.time_off];
    case "employee_trigger":
      return [ApiWorkflowConditionEntityTypes.employee];
  }
};

export const resolveTriggerConditionMode = (
  isCandidateSubject: boolean,
  isTimeOffTrigger: boolean
): WorkflowConditionMode => {
  if (isCandidateSubject) return "candidate";
  return isTimeOffTrigger ? "time_off_trigger" : "employee_trigger";
};

export const resolveBlockConditionMode = (isCandidateSubject: boolean): WorkflowConditionMode =>
  isCandidateSubject ? "candidate" : "block";

export const getFieldsForEntityType = (
  entityType: ApiWorkflowConditionEntityTypes
): ApiIdTitle[] => {
  const fields: Record<ApiWorkflowConditionEntityTypes, ApiIdTitle[]> = {
    [ApiWorkflowConditionEntityTypes.employee]: [
      {
        id: ApiWorkflowConditionFieldTypes.employee_position_department_id,
        title: getApiWorkflowConditionFieldTypesTitle(
          ApiWorkflowConditionFieldTypes.employee_position_department_id
        ),
      },
      {
        id: ApiWorkflowConditionFieldTypes.employee_position_division_id,
        title: getApiWorkflowConditionFieldTypesTitle(
          ApiWorkflowConditionFieldTypes.employee_position_division_id
        ),
      },
      {
        id: ApiWorkflowConditionFieldTypes.employee_employment_legal_entity_id,
        title: getApiWorkflowConditionFieldTypesTitle(
          ApiWorkflowConditionFieldTypes.employee_employment_legal_entity_id
        ),
      },
      {
        id: ApiWorkflowConditionFieldTypes.employee_position_location_id,
        title: getApiWorkflowConditionFieldTypesTitle(
          ApiWorkflowConditionFieldTypes.employee_position_location_id
        ),
      },
      {
        id: ApiWorkflowConditionFieldTypes.employee_position_job_profile_id,
        title: getApiWorkflowConditionFieldTypesTitle(
          ApiWorkflowConditionFieldTypes.employee_position_job_profile_id
        ),
      },
    ],
    [ApiWorkflowConditionEntityTypes.event]: [],
    [ApiWorkflowConditionEntityTypes.time_off]: [
      {
        id: ApiWorkflowConditionFieldTypes.time_off_category_id,
        title: getApiWorkflowConditionFieldTypesTitle(
          ApiWorkflowConditionFieldTypes.time_off_category_id
        ),
      },
    ],
    [ApiWorkflowConditionEntityTypes.candidate]: [
      ApiWorkflowConditionFieldTypes.candidate_stage_id,
      ApiWorkflowConditionFieldTypes.candidate_previous_stage_id,
      ApiWorkflowConditionFieldTypes.candidate_pipeline_id,
      ApiWorkflowConditionFieldTypes.candidate_disqualification_reason_id,
    ].map((id) => ({ id, title: getApiWorkflowConditionFieldTypesTitle(id) })),
    [ApiWorkflowConditionEntityTypes.job]: [
      ApiWorkflowConditionFieldTypes.job_id,
      ApiWorkflowConditionFieldTypes.job_department_id,
      ApiWorkflowConditionFieldTypes.job_division_id,
      ApiWorkflowConditionFieldTypes.job_location_id,
    ].map((id) => ({ id, title: getApiWorkflowConditionFieldTypesTitle(id) })),
  };

  return fields[entityType] || [];
};

export const getFieldTypeByFieldName = (
  fieldName: ApiWorkflowConditionFieldTypes
): ApiWorkflowConditionFieldType => {
  const fieldTypeMap: Record<ApiWorkflowConditionFieldTypes, ApiWorkflowConditionFieldType> = {
    [ApiWorkflowConditionFieldTypes.employee_position_department_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.employee_position_division_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.employee_employment_legal_entity_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.employee_position_location_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.employee_position_job_profile_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.time_off_category_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.candidate_stage_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.candidate_previous_stage_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.candidate_pipeline_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.candidate_disqualification_reason_id]:
      ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.job_id]: ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.job_department_id]: ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.job_division_id]: ApiWorkflowConditionFieldType.array_integer,
    [ApiWorkflowConditionFieldTypes.job_location_id]: ApiWorkflowConditionFieldType.array_integer,
  };

  return fieldTypeMap[fieldName] || ApiWorkflowConditionFieldType.string;
};
